import { useCallback, useEffect, useRef, useState } from "react";

import type { AvailableModel } from "./model-selection-api";
import { fetchModelConfig, updateModelSelection } from "./model-selection-api";

export interface UseModelSelectionReturn {
  currentModelId: string | null;
  availableModels: readonly AvailableModel[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  saveSuccess: string | null;
  selectModel: (modelId: string) => Promise<void>;
  refresh: () => void;
}

export function useModelSelection(): UseModelSelectionReturn {
  const [currentModelId, setCurrentModelId] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<readonly AvailableModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const latestLoad = useRef(0);

  /* Only the load started last may commit, including clearing `isLoading`:
     an earlier one that answers after it would otherwise overwrite the newer
     config, or end the loading state while the newer load is in flight. A
     save supersedes every load in flight the same way. */
  const loadConfig = useCallback(async () => {
    const load = ++latestLoad.current;
    try {
      const config = await fetchModelConfig();
      if (load !== latestLoad.current) return;
      setCurrentModelId(config.currentModelId);
      setAvailableModels(config.availableModels);
      setError(null);
    } catch (e) {
      if (load !== latestLoad.current) return;
      setError(e instanceof Error ? e.message : "Failed to load model configuration");
    } finally {
      if (load === latestLoad.current) setIsLoading(false);
    }
  }, []);

  /* At most one model change is in flight at a time. assistant-api keeps no
     version of the model parameter, so of two concurrent changes the one it
     finishes last wins, whatever the console shows; a change asked for while
     another is in flight is ignored and sends nothing. A save is also
     authoritative over refresh: a refresh asked for while it is in flight is
     ignored, since its answer could only be the config the save replaces. */
  const saveInFlight = useRef(false);

  const refresh = useCallback(() => {
    if (saveInFlight.current) return;
    setIsLoading(true);
    setSaveSuccess(null);
    void loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const selectModel = useCallback(async (modelId: string) => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    latestLoad.current += 1;
    setIsLoading(false);
    setIsSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const result = await updateModelSelection(modelId);
      setCurrentModelId(result.modelId);
      setSaveSuccess(result.message);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update model selection");
    } finally {
      saveInFlight.current = false;
      setIsSaving(false);
    }
  }, []);

  return {
    currentModelId,
    availableModels,
    isLoading,
    isSaving,
    error,
    saveSuccess,
    selectModel,
    refresh,
  };
}
